import './Button.scss'
import type { ComponentProps, ReactNode } from 'react'
import { clx } from '../clx'
import { Spinner } from '../Spinner'

export interface ButtonProps extends ComponentProps<'button'> {
  /** Visual weight. `danger` is for destructive actions; it is never the default focus. */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
  /**
   * `pill` for a prominent call to action, `rounded` for dense in-context
   * actions. Unset: rounded, unless a container (PageHeader, EmptyState)
   * asks for pills through `--button-context-radius` (ADR 0019).
   */
  shape?: 'pill' | 'rounded'
  /** Shows a spinner, sets aria-busy and blocks clicks while keeping focus. */
  isLoading?: boolean
  /** Leading icon (decorative; it gets aria-hidden). */
  icon?: ReactNode
  /** Stretch to the container's inline size. */
  isBlock?: boolean
}

export function Button({
  variant = 'secondary',
  size = 'md',
  shape,
  isLoading = false,
  isBlock = false,
  icon,
  className,
  children,
  disabled,
  onClick,
  ref,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      {...rest}
      disabled={disabled}
      aria-busy={isLoading || undefined}
      aria-disabled={isLoading || undefined}
      onClick={
        isLoading
          ? (event) => {
              event.preventDefault()
            }
          : onClick
      }
      className={clx(
        'button',
        `button--as-${variant}`,
        size !== 'md' && `button--is-${size}`,
        shape && `button--is-${shape}`,
        isBlock && 'button--is-block',
        (icon || isLoading) && 'button--has-icon',
        className
      )}
    >
      {isLoading ? (
        <Spinner size="sm" className="button__icon" />
      ) : (
        icon && (
          <span className="button__icon" aria-hidden="true">
            {icon}
          </span>
        )
      )}
      {children}
    </button>
  )
}
